import { HoldingType } from '@ddt-web/config';
import { BaseHolding, PortfolioChild, PortfolioReference, WebUserDataConstants } from '@ddt-web/domain';
import { Memoize } from '@ddt-web/generic-utils';
import { PortfolioIdUtil } from '@ddt-web/portfolio-data';
import { WweSecurity } from '@ddt-web/wealth-web';
import * as get from 'lodash/get';
import * as has from 'lodash/has';

export class BlendPortfolioUtil {

	/**
	 * Checks the holding's identifier to check if it is a UUID and therefore a user portfolio, vs a security
	 * @param holding holding
	 */
	@Memoize()
	public static isUserPortfolio(holding: BaseHolding): boolean {
		if (!holding) {
			return false;
		}

		const isModel = this.isModel(holding);
		const isSleeve = this.isAPMSleeve(holding);
		const isPortfolio = (holding.identifierType === WebUserDataConstants.IDENTIFIER_TYPE.CUSTOM && has(holding, 'portfolio'))
			|| has(holding, 'children')
			|| holding.type === WebUserDataConstants.TYPE.PORTFOLIO
			|| holding.subType === WebUserDataConstants.CHILD_SUBTYPE.USER_PORTFOLIO;

		const result = !isModel && !isSleeve && isPortfolio;
		return result;
	}

	/**
	 * Checks if the provided holding is a valid partner sleeve.
	 * @param holding holding
	 */
	@Memoize()
	public static isAPMSleeve(holding: BaseHolding): boolean {
		if (!holding) {
			return false;
		}

		const result = get(holding, 'originPlatform.type') === HoldingType.APM;
		return result;
	}

	/**
	 * Checks to see if the holding is a portfolio.
	 * @param holding holding
	 */
	@Memoize()
	public static isChildPortfolio(holding: {}): boolean {
		if (!holding) {
			return false;
		}

		const result = this.isModel(holding) || this.isUserPortfolio(holding) || this.isAPMSleeve(holding);
		return result;
	}

	/**
	 * Checks if a portfolio has a child portfolio or not.
	 * @param portfolio portfolio
	 */
	@Memoize()
	public static isBlend(portfolio: PortfolioChild): boolean {
		if (!portfolio) {
			return false;
		}

		const result = get(portfolio, 'childrenRef', []).some(holding => holding instanceof PortfolioReference || this.isChildPortfolio(holding));
		return result;
	}

	/**
	 * Checks if a portfolio is a BLK model.
	 * @param portfolio portfolio
	 */
	@Memoize()
	public static isModel(portfolio: BaseHolding | PortfolioChild): boolean {
		return this.isBlackRockModel(portfolio) || this.isThirdPartyModel(portfolio);
	}

	/**
	 * Checks if a portfolio is a BLK model.
	 * @param portfolio portfolio
	 */
	@Memoize()
	public static isBlackRockModel(portfolio: BaseHolding | PortfolioChild): boolean {
		if (!portfolio) {
			return false;
		}

		const modelId = get(portfolio, 'identifier') || get(portfolio, 'id');
		const result = PortfolioIdUtil.isBlackRockModel(modelId);
		return result;
	}

	/**
	 * Checks if a portfolio is a third party model.
	 * @param portfolio portfolio
	 */
	@Memoize()
	public static isThirdPartyModel(portfolio: BaseHolding | PortfolioChild): boolean {
		if (!portfolio) {
			return false;
		}

		const modelId = get(portfolio, 'identifier') || get(portfolio, 'id');
		const result = PortfolioIdUtil.isThirdPartyModel(modelId);
		return result;
	}

	/**
	 * Sorts AWE security search results by field priority as implemented.
	 * @param searchResults search results.
	 */
	@Memoize()
	public static sortSearchResults(searchResults: WweSecurity[]): void {
		searchResults.sort((a1, a2) => {
			const compareDesc = a1.description?.localeCompare(a2.description);
			const compareMarket = a1.market?.localeCompare(a2.market);
			const compareCcy = a1.currency?.localeCompare(a2.currency);
			const compareIsin = a1.isin?.localeCompare(a2.isin);
			const compareTicker = a1.ticker?.localeCompare(a2.ticker);

			return compareDesc || compareMarket || compareCcy || compareIsin || compareTicker;
		});
	}
}
